import { useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import Divider from '@mui/material/Divider'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import indlusLogo from '../../../assets/indlusr.png'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { usePlatformAuth } from '../context/PlatformAuthContext'
import { PERM } from '../platformRoles'
import PasswordDialog from './PasswordDialog'

interface NavLeaf {
    to: string
    label: string
    /** Permissão necessária para ver o item. Sem `perm` → base (todos os admins). */
    perm?: string
}

interface NavGroup {
    label: string
    children: NavLeaf[]
}

type NavEntry = NavLeaf | NavGroup

const NAV: NavEntry[] = [
    { to: '/tenants', label: 'Empresas' },
    { to: '/helpdesk', label: 'Suporte', perm: PERM.SUPORTE_MANAGE },
    {
        label: 'Subscrições',
        children: [
            { to: '/planos', label: 'Planos', perm: PERM.PLANOS_MANAGE },
            { to: '/addons', label: 'Add-ons', perm: PERM.PLANOS_MANAGE },
            { to: '/subscricoes', label: 'Subscrições', perm: PERM.SUBSCRICOES_MANAGE },
            { to: '/cobrancas', label: 'Cobranças', perm: PERM.FATURACAO_MANAGE },
            { to: '/pagamentos', label: 'Pagamentos', perm: PERM.FATURACAO_MANAGE },
        ],
    },
    { to: '/admins', label: 'Administradores', perm: PERM.ADMINS_MANAGE },
]

const navButtonSx = {
    color: 'text.secondary',
    fontWeight: 600,
    // Cantos menos arredondados que o default do tema (10px) para os itens do topo.
    borderRadius: '6px',
    '&.active': { color: 'primary.main', bgcolor: '#EEF2FF' },
} as const

function NavGroupButton({ group }: { group: NavGroup }) {
    const [anchor, setAnchor] = useState<null | HTMLElement>(null)
    const { pathname } = useLocation()
    const active = group.children.some((c) => pathname === c.to || pathname.startsWith(`${c.to}/`))

    return (
        <>
            <Button
                size="small"
                onClick={(e) => setAnchor(e.currentTarget)}
                endIcon={<ExpandMoreIcon />}
                sx={{ ...navButtonSx, ...(active ? { color: 'primary.main', bgcolor: '#EEF2FF' } : {}) }}
            >
                {group.label}
            </Button>
            <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
                {group.children.map((child) => (
                    <MenuItem
                        key={child.to}
                        component={NavLink}
                        to={child.to}
                        onClick={() => setAnchor(null)}
                        sx={{ '&.active': { color: 'primary.main', bgcolor: '#EEF2FF' } }}
                    >
                        {child.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}

export default function PlatformLayout() {
    const { admin, logout, refreshMe, hasPerm } = usePlatformAuth()
    const { notify } = useNotification()
    const navigate = useNavigate()
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
    const [pwOpen, setPwOpen] = useState(false)

    // Itens de topo visíveis conforme as permissões do admin. Um grupo só
    // aparece se sobrar pelo menos um filho visível.
    const nav = NAV
        .map((item) =>
            'children' in item
                ? { ...item, children: item.children.filter((c) => !c.perm || hasPerm(c.perm)) }
                : item,
        )
        .filter((item) => ('children' in item ? item.children.length > 0 : !item.perm || hasPerm(item.perm)))

    const handleLogout = async () => {
        setMenuAnchor(null)
        await logout()
        navigate('/login', { replace: true })
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Box
                component="header"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    rowGap: 1,
                    columnGap: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid #E5E7EB',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box component="img" src={indlusLogo} alt="Indlus" sx={{ height: 34, width: 'auto', display: 'block' }} />
                    </Box>
                    <Box component="nav" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {nav.map((item) =>
                            'children' in item ? (
                                <NavGroupButton key={item.label} group={item} />
                            ) : (
                                <Button
                                    key={item.to}
                                    component={NavLink}
                                    to={item.to}
                                    size="small"
                                    sx={navButtonSx}
                                >
                                    {item.label}
                                </Button>
                            ),
                        )}
                    </Box>
                </Box>

                <Button
                    size="small"
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    endIcon={<ExpandMoreIcon />}
                    sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 500, borderRadius: '6px' }}
                >
                    {admin?.email}
                </Button>
                <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                    <MenuItem onClick={() => { setMenuAnchor(null); navigate('/settings') }}>
                        <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
                        Definições da plataforma
                    </MenuItem>
                    <MenuItem onClick={() => { setMenuAnchor(null); setPwOpen(true) }}>
                        <ListItemIcon><LockResetOutlinedIcon fontSize="small" /></ListItemIcon>
                        Alterar a minha palavra-passe
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                        <ListItemIcon><LogoutOutlinedIcon fontSize="small" /></ListItemIcon>
                        Terminar sessão
                    </MenuItem>
                </Menu>
            </Box>

            <Box component="main" sx={{ px: { xs: 2, sm: 3 }, py: 4, maxWidth: 1100, mx: 'auto' }}>
                <Outlet />
            </Box>

            <PasswordDialog
                open={pwOpen}
                title="Alterar a minha palavra-passe"
                requireCurrent
                onClose={() => setPwOpen(false)}
                onSaved={() => { setPwOpen(false); notify('Palavra-passe alterada.'); refreshMe() }}
                submit={async ({ passwordActual, novaPassword }) => {
                    await api.put('/api/admin/auth/password', { passwordActual, novaPassword })
                }}
            />
        </Box>
    )
}
